import type { EventType } from './types';
import type { ProjectContextValue } from '@core/context';

import { type PLAN_NAME, type PlanCadence, PlanType, planNameToType } from '@readme/plans';

export const planTypeToName = {
  [PlanType.FREE]: 'freelaunch2026',
  [PlanType.TRIAL]: 'free',
  [PlanType.STARTUP]: 'startup2018',
  [PlanType.BUSINESS]: 'business2018',
  [PlanType.PRO]: 'pro2026',
  [PlanType.ENTERPRISE]: 'enterprise',
};

export const planTypeToNameAnnual = {
  [PlanType.FREE]: 'freelaunch2026',
  [PlanType.TRIAL]: 'free',
  [PlanType.STARTUP]: 'startup-annual-2024',
  [PlanType.BUSINESS]: 'business-annual-2024',
  [PlanType.PRO]: 'pro2026-annual',
  [PlanType.ENTERPRISE]: 'enterprise',
};

export const planTypeToDisplayName: Record<PlanType, EventType> = {
  [PlanType.FREE]: 'Starter',
  [PlanType.TRIAL]: 'Starter',
  [PlanType.BUSINESS]: 'Business',
  [PlanType.PRO]: 'Pro',
  [PlanType.STARTUP]: 'Startup',
  [PlanType.ENTERPRISE]: 'Enterprise',
  [PlanType.DEV_DASH]: 'DeveloperDashboard',
};

/**
 * Normalize the plan name by removing an appended years if they exists and identifying those as legacy plans.
 * We use `planTrial` as the source of truth which is calculated in the `getPlanTrial` method.
 */
export function normalizePlan(plan: ProjectContextValue['project']['planTrial']): {
  isLegacy: boolean;
  plan: PlanType;
} {
  let isLegacy = false;

  // Extract the plan name and year from the plan type to identify legacy plans
  const regex = /(\D+)(\d*)/;
  const matches = plan.match(regex);
  // eslint-disable-next-line prefer-const
  let [, planName = '', year] = matches || [];
  // For certain plans if we don't have a year then we mark it as legacy plan
  if (['startup', 'business'].includes(planName) && !year) {
    isLegacy = true;
  }

  const planType = planNameToType[plan] ?? PlanType.FREE;

  return { plan: planType, isLegacy };
}

export enum CTA {
  CURRENT = 'CURRENT',
  DOWNGRADE = 'DOWNGRADE',
  ENTERPRISE = 'ENTERPRISE',
  SWITCH = 'SWITCH',
  TRIAL = 'TRIAL',
  UPGRADE = 'UPGRADE',
}

interface DetermineCTAParams {
  currentCadence: PlanCadence;
  currentPlan: PlanType;
  currentPlanName?: PLAN_NAME;
  planNames?: readonly PLAN_NAME[];
  type: PlanType;
  viewCadence: PlanCadence;
}

export function determineCTA({
  currentCadence,
  currentPlan,
  currentPlanName,
  planNames,
  type,
  viewCadence,
}: DetermineCTAParams): { cta: CTA; isCurrentPlan: boolean } {
  const isSamePlanFamily = currentPlanName && planNames ? planNames.includes(currentPlanName) : currentPlan === type;
  const isSameCadence = currentCadence === viewCadence;
  const isTrial = currentPlan === PlanType.TRIAL;
  // Cadence doesn't matter for the free plan
  const isCurrentPlan = isSamePlanFamily && (isSameCadence || type === PlanType.FREE);

  let cta: CTA;
  switch (true) {
    case type === PlanType.ENTERPRISE:
      cta = CTA.ENTERPRISE;
      break;
    case currentPlan != null && type < currentPlan:
      cta = CTA.DOWNGRADE;
      break;
    case isTrial:
      cta = CTA.TRIAL;
      break;
    case isCurrentPlan:
      cta = CTA.CURRENT;
      break;
    case isSamePlanFamily && !isSameCadence:
      cta = CTA.SWITCH;
      break;
    default:
      cta = CTA.UPGRADE;
  }

  return { cta, isCurrentPlan: !!isCurrentPlan };
}

export function formatToCurrency(amount: number, currency: string, annual = false) {
  const calculatedAmount = annual ? amount / 12 : amount;
  return new Intl.NumberFormat(navigator?.language ?? 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(calculatedAmount / 100);
}
